import { API_ENDPOINTS } from "../../enums/apiUrls";
import { logError } from "../../utils/customLogger";
import { getBearerToken } from "./bearerToken";

/** POST body for RPA `v1/nycbs_pharma/initiate-biosimilars`. */
export interface InitiateBiosimilarsPayload {
  parent_identifier: string;
  alternatives: string[];
  force: boolean;
}

/**
 * Triggers biosimilar retry for an order with the selected alternative drugs.
 *
 * @param payload - Row identifier as `parent_identifier`, selected alternatives, and force flag
 * @returns Parsed JSON response from RPA service
 * @throws {Error} When auth is missing or the API returns a non-OK status
 */
export const initiateBiosimilars = async (
  payload: InitiateBiosimilarsPayload,
): Promise<unknown> => {
  try {
    const token = await getBearerToken();
    if (!token) {
      throw new Error("Bearer token not available");
    }

    const response = await fetch(API_ENDPOINTS.INITIATE_BIOSIMILARS, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        accept: "application/json",
      },
      body: JSON.stringify(payload),
    });

    const responseText = await response.text();
    if (!response.ok) {
      throw new Error(
        `Failed to initiate biosimilars: ${response.status} - ${responseText}`,
      );
    }

    if (!responseText.trim()) {
      return null;
    }

    try {
      return JSON.parse(responseText) as unknown;
    } catch {
      return responseText;
    }
  } catch (error) {
    logError(error as Error, "initiateBiosimilars");
    throw error;
  }
};
